import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { storage } from '../lib/storage';

export type Settings = {
  theme: 'system' | 'light' | 'dark';
  density: 'comfortable' | 'compact';
  enableDevTools: boolean;
  apiBaseOverride: string;
  qboAutoMasterSyncHours: number;
};

type SettingsContextType = {
  settings: Settings;
  setSettings: React.Dispatch<React.SetStateAction<Settings>>;
};

const defaultSettings: Settings = {
  theme: 'light',
  density: 'comfortable',
  enableDevTools: true,
  apiBaseOverride: '',
  qboAutoMasterSyncHours: 12,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const LS_KEY = 'rpb:settings:v1';

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    const stored = storage.getJson<Partial<Settings>>(LS_KEY, null);
    return stored ? { ...defaultSettings, ...stored } : { ...defaultSettings };
  });

  useEffect(() => {
    storage.setJson(LS_KEY, settings);
  }, [settings]);

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

    const applyTheme = () => {
      const prefersDark = mediaQuery?.matches ?? false;
      const dark = settings.theme === 'dark' || (settings.theme === 'system' && prefersDark);
      root.dataset.theme = dark ? 'dark' : 'light';
      root.dataset.density = settings.density;
      root.classList.toggle('dark', dark);
    };

    applyTheme();

    if (settings.theme === 'system' && mediaQuery) {
      const listener = () => applyTheme();
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', listener);
      } else {
        mediaQuery.addListener(listener);
      }
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', listener);
        } else {
          mediaQuery.removeListener(listener);
        }
      };
    }

    return undefined;
  }, [settings.theme, settings.density]);

  const value = useMemo(() => ({ settings, setSettings }), [settings]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
